// The single Nakama touch point.
//
// Mirrors `lib/core/network/nakama_gateway.dart`, including the rule that
// makes it worth having: NOTHING else in this codebase touches
// `socket.sendMatchState` or subscribes to `socket.onmatchdata`. One place
// binds the socket, one place decodes, one place sends — so a reconnect cannot
// leave two listeners delivering every event twice, which is the bug the Dart
// version's subscription guard exists to prevent.

import { Client, type Session, type Socket } from '@heroiclabs/nakama-js';
import { nakamaUseSsl } from '../config';
import { decodeMatchData } from '../protocol/decode';
import type { MatchSender } from '../protocol/encode';
import type { MatchEvent } from '../protocol/messages';

export type MatchEventListener = (event: MatchEvent) => void;
export type ConnectionListener = () => void;

/**
 * Owns the socket and the match membership.
 *
 * Deliberately NOT a general-purpose event emitter: one event listener, one
 * disconnect listener. More than one of either has always meant, in the Dart
 * original, that two subsystems were racing to interpret the same frame.
 */
export class MatchGateway implements MatchSender {
  private socket: Socket | null = null;
  private session: Session | null = null;
  private matchId: string | null = null;

  /** Bumped on every bind, so a stale socket's callbacks can be ignored. */
  private generation = 0;

  private onEvent: MatchEventListener = () => {};
  private onDisconnect: ConnectionListener = () => {};

  constructor(private readonly client: Client) {}

  get currentMatchId(): string | null {
    return this.matchId;
  }

  get userId(): string {
    return this.session?.user_id ?? '';
  }

  get username(): string {
    return this.session?.username ?? '';
  }

  get isConnected(): boolean {
    return this.socket !== null;
  }

  listen(onEvent: MatchEventListener, onDisconnect: ConnectionListener): void {
    this.onEvent = onEvent;
    this.onDisconnect = onDisconnect;
  }

  /**
   * Opens (or replaces) the socket for [session].
   *
   * `createStatus: false` — this renderer has no friends list and no presence
   * following, so publishing an online status would be pure noise on a server
   * whose config has `single_socket: true`.
   */
  async connect(session: Session): Promise<void> {
    this.session = session;

    // Replace, never accumulate. `disconnect(false)` suppresses the close
    // event so tearing down the OLD socket does not fire the disconnect
    // handler and start a reconnect for a socket we are deliberately dropping.
    this.teardown();

    const socket = this.client.createSocket(nakamaUseSsl, false);
    const myGeneration = ++this.generation;

    socket.onmatchdata = (matchData) => {
      if (myGeneration !== this.generation) return; // superseded socket
      const event = decodeMatchData(Number(matchData.op_code), toBytes(matchData.data));
      if (event) this.onEvent(event);
    };

    socket.ondisconnect = () => {
      if (myGeneration !== this.generation) return;
      this.socket = null;
      this.onDisconnect();
    };

    socket.onerror = (evt) => {
      // An error is NOT a disconnect: the browser fires both, in that order,
      // for a dropped connection. Reacting here as well would start two
      // reconnects for one drop — SingleFlight would coalesce them, but the
      // clearer contract is that only `ondisconnect` triggers recovery.
      console.warn('[gateway] socket error', evt);
    };

    await socket.connect(session, false);
    this.socket = socket;
  }

  /** Joins (or rejoins) a match. The server treats both as the same message. */
  async joinMatch(matchId: string): Promise<void> {
    if (!this.socket) throw new Error('joinMatch before connect');
    const match = await this.socket.joinMatch(matchId);
    this.matchId = match.match_id ?? matchId;
  }

  /**
   * Reconnects and rejoins in one attempt, resolving false rather than
   * throwing.
   *
   * One attempt, not a loop — the loop lives in reconnect.ts so its timing is
   * testable without a socket, exactly as the Dart version splits
   * `runReconnectLoop` from `retryJoinMatch`.
   */
  async reattach(): Promise<boolean> {
    if (!this.session || !this.matchId) return false;
    try {
      await this.connect(this.session);
      await this.joinMatch(this.matchId);
      return true;
    } catch (err) {
      console.warn('[gateway] reattach failed', err);
      return false;
    }
  }

  /** The one send point. Payloads go out as JSON strings, as on the Dart side. */
  send(opCode: number, payload: unknown): void {
    const socket = this.socket;
    const matchId = this.matchId;
    if (!socket || !matchId) {
      // Dropped rather than queued, on purpose. A queued input replayed after
      // a reconnect lands in a round the server has already scored — the
      // resync path re-reads authoritative state instead, and the server's
      // late-submission window covers a genuinely in-flight submit.
      console.warn('[gateway] dropped a send with no live match', opCode);
      return;
    }
    void socket.sendMatchState(matchId, opCode, JSON.stringify(payload)).catch((err) => {
      console.warn('[gateway] send failed', opCode, err);
    });
  }

  /** Leaves the match and closes the socket. Safe to call more than once. */
  async close(): Promise<void> {
    const socket = this.socket;
    const matchId = this.matchId;
    this.generation++; // silence the old socket's callbacks first
    this.socket = null;
    this.matchId = null;
    if (socket && matchId) {
      try {
        await socket.leaveMatch(matchId);
      } catch {
        /* leaving a match we may already have been removed from */
      }
    }
    socket?.disconnect(false);
  }

  private teardown(): void {
    const old = this.socket;
    this.socket = null;
    old?.disconnect(false);
  }
}

/**
 * Normalises whatever the SDK hands us into bytes.
 *
 * `MatchData.data` is typed as a Uint8Array but arrives as a base64 string on
 * some transports and as a plain array after a JSON round trip. Handling all
 * three here keeps the decoder pure.
 */
function toBytes(data: unknown): Uint8Array | null {
  if (data == null) return null;
  if (data instanceof Uint8Array) return data;
  if (Array.isArray(data)) return new Uint8Array(data);
  if (typeof data === 'string') {
    try {
      const binary = atob(data);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
      return out;
    } catch {
      return new TextEncoder().encode(data);
    }
  }
  return null;
}
